import { useEffect, useMemo } from 'react'

// import { getCurrentBaseUrl } from 'api/utils'
// import { Partytown } from '@builder.io/partytown/react'
import parse from 'html-react-parser'
import Head from 'next/head'

import { getHelloSitesUrl } from 'api/utils/paths'
import { getWatermark } from 'api/utils/watermark'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  assetImagePath,
  basePath,
  ENV,
  FAVICONS,
  LOCALE_SPECS,
  TRANSLATE_LANGUAGES,
} from 'interfaces/constants'
import { TranslateOption } from 'interfaces/types'

export type SEOData = {
  currentPage?: number
  totalPages?: number
  title?: string
  description?: string
  type?: 'website' | 'article' | 'profile'
  image?: string
  imageWidth?: string | number
  imageHeight?: string | number
  siteName?: string
  customMetaData?: Array<{
    [key: string]: string
  }>
  isIndexable?: boolean
  openGraph?: {
    title?: string
    description?: string
    image?: string
    imageWidth?: number
    imageHeight?: number
  }
  twitter?: {
    title?: string
    description?: string
    image?: string
  }
  apiMetaData?: string
  schemaData?: string
  hasNoCanonical?: boolean
}

export type Props = {
  SEOData?: SEOData
  noUsingRouterPush?: boolean
  languageOptions?: Array<TranslateOption>
  isHealthToolResultSharing?: boolean
}
const DiscoverHead = ({
  SEOData = {},
  noUsingRouterPush = false,
  languageOptions = [],
  isHealthToolResultSharing,
}: Props) => {
  const { DEPLOY_ENV } = ENV
  const isProduction = DEPLOY_ENV === 'production'
  const { asPath } = useRouterExtend()

  const {
    totalPages,
    currentPage,
    title = '',
    description = '',
    image: imageSEO,
    type,
    customMetaData = [],
    isIndexable = true,
    hasNoCanonical = false,
    openGraph,
    twitter,
  } = SEOData
  const {
    title: ogTitle,
    image: ogImgProps,
    imageWidth: ogImgWidth = '1200',
    imageHeight: ogImgHeight = '630',
    description: ogDesc,
  } = openGraph || {}
  const {
    image: twitterImgProps,
    title: twitterTitle,
    description: twitterDesc,
  } = twitter || {}
  const robotIndex =
    isIndexable && isProduction
      ? 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      : 'noindex'
  const {
    locale,
    router: { pathname, query, push },
  } = useRouterExtend()

  const defaultImage = `https://${domainLocales[locale]}${assetImagePath}/${locale}.png`

  // https://hhgdev.atlassian.net/browse/HHD-6372
  const image = getWatermark(locale, imageSEO) || defaultImage
  const ogImg = getWatermark(locale, ogImgProps)
  const twitterImg = getWatermark(locale, twitterImgProps)

  let newPath = `${basePath}${pathname}`
  Object.keys(query).forEach((key) => {
    const queryItem = query[key]
    if (queryItem && Array.isArray(queryItem)) {
      newPath = newPath.replace(`[...${key}]`, queryItem.join('/'))
    }
    /** For example: /bot/[slug] -> /bot/lorem-ipsum */
    if (typeof queryItem === 'string' && pathname.endsWith(`[${key}]`)) {
      newPath = newPath.replace(`[${key}]`, queryItem)
    }
  })

  const hrefLangs = useMemo(() => {
    if (languageOptions?.length >= 2) {
      return languageOptions.map((option) => {
        if (option.label === TRANSLATE_LANGUAGES.EN) {
          return {
            ...option,
            label: 'en-PH',
          }
        } else if (option.label === TRANSLATE_LANGUAGES.FIL) {
          return {
            ...option,
            label: 'tl-PH',
          }
        }
        return option
      })
    }
    return []
  }, [languageOptions])

  useEffect(() => {
    const replaceClick = (e: MouseEvent) => {
      if (e && e.target) {
        const target = e.target as HTMLAnchorElement
        if (target.nodeName == 'A') {
          if (
            !target.closest('.no-replace-click') &&
            target.target !== '_blank'
          ) {
            e.preventDefault()
            push(target.href)
          }
        }
      }
    }
    if (false && !noUsingRouterPush) {
      window.document.body.addEventListener('click', replaceClick)
    }

    return () => {
      if (!noUsingRouterPush) {
        window.document.body.removeEventListener('click', replaceClick)
      }
    }
  }, [])

  const hasSlash = newPath === '/' ? '' : '/'

  let metaData: any

  if (SEOData.apiMetaData && typeof SEOData.apiMetaData === 'string') {
    const paginatedTitle = SEOData.apiMetaData?.match(/<title.*?>(.*)<\/title>/)
    const paginatedNumber = ` - Page ${currentPage} of ${totalPages}`
    const fD =
      currentPage && currentPage !== 1
        ? paginatedTitle && paginatedTitle[0] && paginatedTitle[1]
          ? SEOData.apiMetaData.replace(
              paginatedTitle[0],
              paginatedTitle[0].includes('-')
                ? paginatedTitle[0].replace('-', paginatedNumber + ' - ')
                : `<title>${paginatedTitle[1].concat(paginatedNumber)}</title>`,
            )
          : SEOData.apiMetaData
        : SEOData.apiMetaData
    try {
      metaData = parse(fD, {
        trim: true,
        replace: (d: any) => {
          if (d.attribs) {
            Object.keys(d.attribs).forEach((k) => {
              const formatAttr = k.replace(/[^\w\s]/gi, '')
              const lastVal = d.attribs[k]
              delete d.attribs[k]

              if (isNaN(Number(formatAttr))) {
                d.attribs[formatAttr] = lastVal
                if (['og:image', 'twitter:image'].includes(lastVal)) {
                  d.attribs.content =
                    getWatermark(locale, d.attribs.content) || image
                }
                if (
                  isHealthToolResultSharing &&
                  d.attribs?.property === 'og:url' &&
                  d.attribs?.content
                ) {
                  d.attribs.content = `
                  ${getHelloSitesUrl(locale)}${asPath}`
                }
              }
            })
          }
        },
      })
    } catch (e) {
      console.error(e)
    }
    const metaDataImage = metaData?.some(
      (data: any) => data.props?.property === 'og:image',
    )
    if (Array.isArray(metaData) && metaData.length && !metaDataImage) {
      metaData = [
        ...metaData,
        <meta key="og:image" property="og:image" content={ogImg || image} />,
        <meta
          key="og:image:secure_url"
          property="og:image:secure_url"
          content={ogImg || image}
        />,
        <meta
          key="og:image:width"
          property="og:image:width"
          content={`${ogImgWidth}`}
        />,
        <meta
          key="og:image:height"
          property="og:image:height"
          content={`${ogImgHeight}`}
        />,
        <meta
          key="twitter:image"
          name="twitter:image"
          content={twitterImg || image}
        />,
      ]
    }
  }

  const metaDataTitle = metaData?.filter((data: any) => data.type === 'title')
  const pageTitle = metaDataTitle?.[0]?.props?.children || title

  return (
    <Head>
      {/* <Partytown
          debug={process.env.NODE_ENV !== 'production'}
          forward={[
            'dataLayer.push',
            'fbq',
            'ttq.track',
            'ttq.page',
            'ttq.load',
          ]}
          resolveUrl={(url, location, type) => {
            if (type === 'script') {
              if (url.href.includes(location.origin)) return url
              const proxyUrl = new URL(location.origin + '/api/proxy')
              proxyUrl.searchParams.append('url', url.href)
              return proxyUrl
            }
            return url
          }}
        /> */}
      {/* <link rel="preconnect" href={NEXT_PUBLIC_BASE_API_URL} /> */}
      <meta charSet="UTF-8" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
      />
      <meta name="google" content="notranslate" />
      <meta name="robots" content={robotIndex} />

      <meta property="fb:app_id" content="653713035016537" />
      {customMetaData.map((metaData, index) => (
        <meta key={`custom-meta-${index}`} {...metaData} />
      ))}

      <meta name="theme-color" content="#2c87f3" />
      <meta name="page_type" content="Home" />
      <meta name="page_platform" content="WEB" />
      <meta name="page_language" content={locale} />
      {hrefLangs && hrefLangs.length >= 2 ? (
        hrefLangs.map((hrefLang) => (
          <link
            key={hrefLang.value}
            rel="alternate"
            hrefLang={hrefLang.label}
            href={`https://${domainLocales[locale]}${hrefLang.value}`}
          />
        ))
      ) : (
        <link
          rel="alternate"
          href={`https://${domainLocales[locale]}${newPath}${hasSlash}`}
        />
      )}
      <meta
        name="ahrefs-site-verification"
        content="46e7521a2b89a1013149c4685bbe02a1d36a4732fef98655142b829bc87cba4d"
      />

      {!metaData && (
        <>
          <title>{SEOData.openGraph?.title}</title>
          {!hasNoCanonical && (
            <link
              rel="canonical"
              href={`https://${domainLocales[locale]}${newPath}${hasSlash}`}
            />
          )}
          <meta name="description" content={description || ogDesc} />
          {SEOData.schemaData && (
            <script
              type="application/ld+json"
              className="yoast-schema-graph"
              dangerouslySetInnerHTML={{ __html: SEOData.schemaData }}
            ></script>
          )}

          <meta property="og:locale" content={locale} />
          <meta property="og:type" content={type || 'website'} />
          <meta property="og:title" content={ogTitle || title} />
          <meta property="og:description" content={ogDesc || description} />
          {/* The slag at the end of og:url is required by Facebook */}
          <meta
            property="og:url"
            content={`https://${domainLocales[locale]}${newPath}/`}
          />
          <meta
            property="og:site_name"
            content={LOCALE_SPECS[locale].SITE_NAME_FORMATTED}
          />
          <meta property="og:image" content={ogImg || image} />
          <meta property="og:image:secure_url" content={ogImg || image} />
          <meta property="og:image:width" content={`${ogImgWidth}`} />
          <meta property="og:image:height" content={`${ogImgHeight}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={twitterTitle || title} />
          <meta
            name="twitter:description"
            content={twitterDesc || description}
          />
          <meta name="twitter:image" content={twitterImg || image} />
        </>
      )}

      {(FAVICONS[locale] || []).map((item, index) => {
        if (item.type === 'link') {
          return (
            <link
              key={`favicon-${index}`}
              rel={item.rel}
              href={item.href}
              sizes={item.sizes}
            />
          )
        }
        if (item.type === 'meta') {
          return <meta key={index} name={item.name} content={item.content} />
        }
        return null
      })}
      {metaData}
      {pageTitle && <title>{pageTitle}</title>}
      {/* {image && <meta key={'og:image'} property="og:image" content={image} />} */}
    </Head>
  )
}

export { DiscoverHead }
